/**
 * Job Status Mapper Utility
 * Maps job status fields to calendar badge codes and admin-readable labels.
 */

export type StatusBadgeCode = 'IN' | 'PO' | 'COM' | 'DEL' | 'COL' | 'INC' | 'URG';

export type DeliveryResult = 'pending' | 'delivered' | 'incomplete';

export interface JobStatusInput {
  status?: string;
  production_status?: string | null;
  fulfillment_type?: string;
  delivery_result?: DeliveryResult | null;
  collection_completed?: boolean;
}

const FADED_BADGE_OPACITY = 0.6;

/**
 * Map job fields to calendar badge codes.
 * Fulfillment completion (DEL / INC / COL) takes priority over production (COM / IN / PO).
 */
export function getJobStatusBadge(job: JobStatusInput): StatusBadgeCode {
  const {
    status,
    production_status: productionStatus,
    fulfillment_type: fulfillmentType,
    delivery_result: deliveryResult,
    collection_completed: collectionCompleted,
  } = job;

  if (fulfillmentType === 'delivery') {
    if (deliveryResult === 'delivered') return 'DEL';
    if (deliveryResult === 'incomplete') return 'INC';
  }

  if (fulfillmentType === 'collection' && collectionCompleted) {
    return 'COL';
  }

  if (status === 'completed' && productionStatus === 'production_complete') {
    return 'COM';
  }

  if (status === 'production') {
    if (productionStatus === 'problem_orders') return 'PO';
    return 'IN';
  }

  return 'IN';
}

/**
 * Human-readable fulfillment / production status for admins.
 */
export function getAdminJobStatusLabel(job: JobStatusInput): string {
  const badge = getJobStatusBadge(job);

  switch (badge) {
    case 'DEL':
      return 'Delivered';
    case 'INC':
      return 'Incomplete delivery';
    case 'COL':
      return 'Collected';
    case 'COM':
      return 'Production complete';
    case 'PO':
      return 'Problem orders';
    case 'IN':
      return 'In production';
    default:
      return 'In production';
  }
}

/** Whether the badge should render at reduced opacity (delivered or collected only). */
export function isFadedStatusBadge(code: StatusBadgeCode): boolean {
  return code === 'DEL' || code === 'COL';
}

export function getStatusBadgeOpacity(code: StatusBadgeCode): number {
  return isFadedStatusBadge(code) ? FADED_BADGE_OPACITY : 1;
}

/**
 * Border color and opacity for calendar / booking cards.
 */
export function getStatusBorderAndOpacity(statusCode: StatusBadgeCode): {
  borderColor: string;
  opacity: number;
  borderWidth: string;
} {
  const opacity = getStatusBadgeOpacity(statusCode);

  switch (statusCode) {
    case 'IN':
      return { borderColor: '#000000', opacity, borderWidth: '3px' };
    case 'PO':
      return { borderColor: '#f97316', opacity, borderWidth: '3px' };
    case 'COM':
      return { borderColor: 'transparent', opacity, borderWidth: '3px' };
    case 'DEL':
      return { borderColor: '#22c55e', opacity, borderWidth: '3px' };
    case 'COL':
      return { borderColor: '#3b82f6', opacity, borderWidth: '3px' };
    case 'INC':
      return { borderColor: '#f59e0b', opacity, borderWidth: '3px' };
    case 'URG':
      return { borderColor: '#ef4444', opacity, borderWidth: '3px' };
    default:
      return { borderColor: '#000000', opacity: 1, borderWidth: '3px' };
  }
}

/** Tailwind classes for admin status pills. */
export function getAdminJobStatusPillClassNames(job: JobStatusInput): string {
  const badge = getJobStatusBadge(job);

  switch (badge) {
    case 'DEL':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'INC':
      return 'bg-amber-100 text-amber-800 border-amber-300';
    case 'COL':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'COM':
      return 'bg-gray-100 text-gray-800 border-gray-300';
    case 'PO':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-300';
  }
}
